import {
  overallDevelopmentCardConfig,
  overviewCardConfig
} from '../../config/analytics/analyticsCardsConfig';
import { FACEBOOK, INSTAGRAM } from '../../config/constants';

interface DataItem {
  date: string;
  value: number | null;
}
interface DateRange {
  startDate: Date;
  endDate: Date;
}

const lastNotNullValue = (values: number[]): number | null => {
  for (let i = values.length - 1; i >= 0; i--) {
    if (values[i] !== null) return values[i];
  }
  return null;
};

const sortAndFilterByDate = (
  data: DataItem[],
  dates: DateRange
): DataItem[] => {
  return data
    .filter(
      (item) =>
        new Date(item.date).setHours(0, 0, 0, 0) >=
          dates.startDate.setHours(0, 0, 0, 0) &&
        new Date(item.date).setHours(0, 0, 0, 0) <=
          dates.endDate.setHours(0, 0, 0, 0)
    )
    .sort(
      (a, b) =>
        new Date(a.date).setHours(0, 0, 0, 0) -
        new Date(b.date).setHours(0, 0, 0, 0)
    );
};

export const transferDataForAnalyticsOverviewCard = (
  res: any,
  dates: DateRange
): any => {
  const data = {};
  Object.keys(overviewCardConfig).forEach((platform) => {
    const config = overviewCardConfig[platform];
    const originalData = res[config.plot_name]?.[config.key];
    if (!originalData) return;

    const dataCopy = JSON.parse(JSON.stringify(originalData));
    data[config.key] = dataCopy;
    data[config.key].data = sortAndFilterByDate(dataCopy.data, dates);

    const values = data[config.key].data.map((item) =>
      item.value !== null ? parseInt(item.value) : null
    );
    data[config.key].displayValue = lastNotNullValue(values) || 0;
    if (config.useSum) {
      // remove null values from the array
      const arr = values.filter((value) => value !== null);
      // sort by date newest to oldest
      arr.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
      // split array into 2
      const midIndex = Math.floor(arr.length / 2);
      const currentMonth = arr.slice(0, midIndex);
      const lastMonth = arr.slice(midIndex);
      // calculate the sum of each half
      const currentMonthSum = currentMonth.reduce((a, b) => (a || 0) + b, 0);
      const lastMonthSum = lastMonth.reduce((a, b) => (a || 0) + b, 0);
      data[config.key].trend = currentMonthSum - lastMonthSum;
      data[config.key].displayValue = currentMonthSum;
      data[config.key].displayPercentage = Math.ceil(
        ((currentMonthSum - lastMonthSum) / (lastMonthSum || 1)) * 100
      );
      data[config.key].oldValue = lastMonthSum;

      // if the first non-null value is 0, set trend to 1 and displayPercentage to 100. This avoids a growth of
      // -100% if the first data is not there
      if (lastMonthSum === 0 && currentMonthSum !== 0) {
        data[config.key].trend = 1;
        data[config.key].displayPercentage = 100;
      } else if (lastMonthSum === 0 && currentMonthSum === 0) {
        data[config.key].trend = 0;
        data[config.key].displayPercentage = 0;
      }
    } else {
      const firstNonNullValue = values.find((value) => value !== null);
      if (firstNonNullValue === undefined) return;
      const trend = data[config.key].displayValue - firstNonNullValue;
      data[config.key].displayPercentage = Math.ceil(
        (data[config.key].displayValue / (firstNonNullValue || 1) - 1) * 100
      );

      data[config.key].trend = trend;
      data[config.key].oldValue = firstNonNullValue;

      // if the first non-null value is 0, set trend to 1 and displayPercentage to 100. This avoids a growth of
      // -100% if the first data is not there
      if (firstNonNullValue === 0 && data[config.key].displayValue !== 0) {
        data[config.key].trend = 1;
        data[config.key].displayPercentage = 100;
      } else if (
        firstNonNullValue === 0 &&
        data[config.key].displayValue === 0
      ) {
        data[config.key].trend = 0;
        data[config.key].displayPercentage = 0;
      }
    }
  });
  return data;
};

export const transferDataForAnalyticsOverallCard = (
  res: any,
  dates: DateRange,
  prvDates: DateRange
): any => {
  console.log('res entred ', res);
  const config = [
    // {
    //   plot_name: 'post_reach_days_28',
    //   key: [FACEBOOK, INSTAGRAM],
    //   mergeWith: 'post_reach',
    //   addTo: 'virtual_post_reach',
    //   overviewKey: 'reach',
    //   daysNumber: 28
    // },
    // {
    //   plot_name: 'page_posts_impressions_28_days',
    //   key: [FACEBOOK, INSTAGRAM],
    //   mergeWith: 'post_impressions',
    //   addTo: 'virtual_post_impressions',
    //   overviewKey: 'impressions',
    //   daysNumber: 28
    // },
    // {
    //   plot_name: 'page_posts_reactions_28_days',
    //   key: [FACEBOOK],
    //   mergeWith: 'post_reactions',
    //   addTo: 'virtual_post_reactions',
    //   overviewKey: 'reactions',
    //   daysNumber: 28
    // }
  ];
  res.virtual_overview = { ...res.overview };

  config.forEach((cfg) => {
    cfg.key.forEach((platformKey) => {
      const platformData = { ...res?.[cfg.plot_name]?.[platformKey] };
      const newPlatformData = [];

      if (!platformData?.data) return;
      const totalDataPoints = platformData.data.length;
      const remainingDataPoints = totalDataPoints % cfg.daysNumber;

      // Loop for the remaining data points
      const lastValue =
        platformData.data[totalDataPoints - remainingDataPoints - 1].value /
        remainingDataPoints;
      for (let i = 0; i < remainingDataPoints; i++) {
        newPlatformData.push({
          value: lastValue,
          date: platformData.data[totalDataPoints - remainingDataPoints + i]
            .date
        });
      }

      // Loop for the complete sets of 28 data points
      for (
        let i = cfg.daysNumber - 1;
        i < totalDataPoints - remainingDataPoints;
        i += cfg.daysNumber
      ) {
        const value = platformData.data[i].value / cfg.daysNumber;
        for (let j = 0; j < cfg.daysNumber; j++) {
          newPlatformData.push({
            value,
            date: platformData.data[i - j].date
          });
        }
      }

      newPlatformData.sort(
        (a, b) =>
          new Date(a.date).setHours(0, 0, 0, 0) -
          new Date(b.date).setHours(0, 0, 0, 0)
      );

      const newData = {
        ...res[cfg.mergeWith],
        ...res[cfg.addTo],
        [platformKey]: { data: newPlatformData }
      };
      res[cfg.addTo] = newData;

      // Preparing new overview
      const postReach = [];

      // Iterate over each platform in newData
      Object.keys(newData).forEach((platform) => {
        // Iterate over each entry for the current platform
        newData[platform]?.data.forEach((entry) => {
          const { date, value } = entry;

          // Find the index of the existing date entry in postReach
          const existingIndex = postReach.findIndex((obj) => obj.date === date);

          if (existingIndex !== -1) {
            // If the date already exists, add the value to the existing entry
            postReach[existingIndex].value += value;
          } else {
            // If the date does not exist, create a new entry
            postReach.push({ date, value });
          }
        });
      });

      const overviewData = {
        ...res.overview,
        ...res.virtual_overview,
        [cfg.overviewKey]: {
          data: postReach.sort(
            (a, b) =>
              new Date(a.date).setHours(0, 0, 0, 0) -
              new Date(b.date).setHours(0, 0, 0, 0)
          )
        }
      };

      res.virtual_overview = overviewData;
    });
  });

  const data = {};
  Object.keys(overallDevelopmentCardConfig).forEach((plot) => {
    const config = overallDevelopmentCardConfig[plot];
    const originalData = res[config.plot_name]?.[config.key];
    if (!originalData) return;

    data[config.key] = JSON.parse(JSON.stringify(originalData));
    data[config.key].data = sortAndFilterByDate(data[config.key].data, dates);

    data[config.key].prvData = sortAndFilterByDate(
      JSON.parse(JSON.stringify(originalData)).data,
      prvDates
    );
    const prvData = sortAndFilterByDate(
      JSON.parse(JSON.stringify(originalData)).data,
      prvDates
    );

    const values = data[config.key].data.map((item) => item.value);
    const prvValues = prvData.map((item) => item.value);
    const currentSum = values.reduce(
      (a, b) => (a || 0) + (b !== null ? b : 0),
      0
    );
    const oldSum =
      prvValues.reduce((a, b) => (a || 0) + (b !== null ? b : 0), 0) || 1;

    const trend = currentSum - oldSum;

    data[config.key].displayPercentage = Math.round(
      (currentSum / oldSum - 1) * 100
    );
    data[config.key].trend = trend;
    data[config.key].displayValue = currentSum;
    data[config.key].oldValue = oldSum;
  });
  return data;
};
